import PageHero from "@/components/PageHero"
import { useScrollReveal } from "@/hooks/useScrollReveal"
import {Target, Users, Globe, Lightbulb, Heart, TrendingUp} from 'lucide-react'

const timeline = [
  { year: "2019", text: "Fundação da English Work com a missão de democratizar o ensino de inglês no Brasil." },
  { year: "2020", text: "Lançamento da plataforma digital EW Academy com mais de 400 horas de conteúdo." },
  { year: "2021", text: "Criação da Lucy IA, tutora virtual disponível 24h via WhatsApp." },
  { year: "2022", text: "Expansão para aplicativos iOS e Android e lançamento do EW Live." },
  { year: "2023", text: "Lançamento do EW Pass, ecossistema completo de aprendizado de inglês." },
  { year: "2024", text: "Milhares de alunos atendidos em todo o Brasil, com foco em impacto social." },
]

const pillars = [
  { icon: Target, title: "Missão", text: "Transformar o aprendizado de inglês em algo acessível, flexível e aplicável à vida real, utilizando tecnologia e inovação para impactar vidas." },
  { icon: Globe, title: "Visão", text: "Ser referência nacional em educação digital de inglês, conectando pessoas a oportunidades globais através do idioma." },
  { icon: Heart, title: "Valores", text: "Acessibilidade, inovação, respeito à rotina do aluno, compromisso com resultados e impacto social positivo." },
]

const diferenciais = [
  { icon: Lightbulb, title: "Tecnologia como aliada", text: "Inteligência artificial, aplicativos e plataforma digital integrada para um aprendizado moderno e eficiente." },
  { icon: Users, title: "Foco no aluno real", text: "Metodologia pensada para adultos com rotina corrida, que precisam de flexibilidade e autonomia." },
  { icon: TrendingUp, title: "Resultados comprovados", text: "Milhares de alunos que destravaram o inglês e transformaram suas carreiras e vidas pessoais." },
]

export default function QuemSomos() {
  const section1 = useScrollReveal()
  const section2 = useScrollReveal()
  const section3 = useScrollReveal()

  return (
    <>
      <PageHero
        title="Quem é a English Work"
        subtitle="Conheça a empresa que está transformando o aprendizado de inglês no Brasil através de tecnologia, inovação e impacto real."
      />

      {/* Sobre */}
      <section className="py-20 bg-white">
        <div
          ref={section1.ref}
          className={`max-w-5xl mx-auto px-5 transition-all duration-700 ${section1.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: "#0E8EA0" }}>
                Nossa história
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                A English Work é uma empresa de educação digital focada em transformar o aprendizado de inglês em algo acessível, flexível e aplicável à vida real.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Atuamos no desenvolvimento de soluções educacionais que respeitam a rotina do aluno moderno — pessoas que trabalham, estudam, têm compromissos e precisam de autonomia para aprender no próprio ritmo.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Mais do que oferecer cursos, a English Work desenvolve ecossistemas de aprendizagem, unindo conteúdo estruturado, tecnologia, inteligência artificial e suporte contínuo.
              </p>
            </div>
            <div className="space-y-4">
              {timeline.map((item) => (
                <div key={item.year} className="flex gap-4 items-start">
                  <div
                    className="shrink-0 w-16 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: "#B038B2" }}
                  >
                    {item.year}
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed pt-1">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Missão, Visão, Valores */}
      <section className="py-20" style={{ backgroundColor: "#F8F9FA" }}>
        <div
          ref={section2.ref}
          className={`max-w-5xl mx-auto px-5 transition-all duration-700 ${section2.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12" style={{ color: "#0E8EA0" }}>
            Missão, Visão e Valores
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300"
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: "#0E8EA015" }}
                >
                  <item.icon size={26} style={{ color: "#0E8EA0" }} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-20 bg-white">
        <div
          ref={section3.ref}
          className={`max-w-5xl mx-auto px-5 transition-all duration-700 ${section3.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12" style={{ color: "#0E8EA0" }}>
            Nossos diferenciais
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {diferenciais.map((item) => (
              <div key={item.title} className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                  style={{ backgroundColor: "#B038B215" }}
                >
                  <item.icon size={28} style={{ color: "#B038B2" }} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
